import type { Condition } from '@axilon-apps/domains-ontology-ui-state'

/**
 * True when a condition tree contains at least one leaf carrying a non-empty
 * `tag_id`. Used to gate submit so a trigger owner can't be saved with a
 * trigger that references no registry tag.
 */
export function conditionHasTagId(condition: Condition): boolean {
  switch (condition.kind) {
    case 'live':
    case 'agg':
    case 'deviation':
    case 'alarm_active':
      return condition.tag_id.trim().length > 0
    case 'expr':
      return Object.values(condition.sources).some((src) => src.tag_id.trim().length > 0)
    case 'and':
    case 'or':
      return condition.operands.some(conditionHasTagId)
    case 'not':
      return conditionHasTagId(condition.operand)
    default:
      return false
  }
}

/**
 * A leaf (or expr source) is unresolved when it names a `tag_id` the proxy
 * could not echo a `tag_path` for on read — the referenced registry tag no
 * longer exists (e.g. the registry was reset after the agent was authored).
 * A freshly picked tag always carries its path, so this is a reliable signal.
 * The proxy rejects any write carrying such a reference, so callers use this
 * to flag the leaf and gate Save before the round trip.
 */
export function isUnresolvedTagRef(ref: { tag_id: string; tag_path?: string }): boolean {
  return ref.tag_id.trim().length > 0 && !(ref.tag_path && ref.tag_path.trim().length > 0)
}

/** Collect the `tag_id`s of every unresolved leaf / expr source (deduped). */
export function collectUnresolvedTagIds(condition: Condition, out: Set<string>): void {
  switch (condition.kind) {
    case 'live':
    case 'agg':
    case 'deviation':
    case 'alarm_active':
      if (isUnresolvedTagRef(condition)) out.add(condition.tag_id.trim())
      return
    case 'expr':
      for (const src of Object.values(condition.sources)) {
        if (isUnresolvedTagRef(src)) out.add(src.tag_id.trim())
      }
      return
    case 'and':
    case 'or':
      for (const operand of condition.operands) collectUnresolvedTagIds(operand, out)
      return
    case 'not':
      collectUnresolvedTagIds(condition.operand, out)
      return
    default:
      return
  }
}

/** Collect every non-empty leaf `tag_id` from a condition tree (deduped). */
export function collectConditionTagIds(condition: Condition, out: Set<string>): void {
  switch (condition.kind) {
    case 'live':
    case 'agg':
    case 'deviation':
    case 'alarm_active': {
      const id = condition.tag_id.trim()
      if (id.length > 0) out.add(id)
      return
    }
    case 'expr':
      for (const src of Object.values(condition.sources)) {
        const id = src.tag_id.trim()
        if (id.length > 0) out.add(id)
      }
      return
    case 'and':
    case 'or':
      for (const operand of condition.operands) collectConditionTagIds(operand, out)
      return
    case 'not':
      collectConditionTagIds(condition.operand, out)
      return
    default:
      return
  }
}
